/**
 * Step 5, Review & generate: every page of the free preview (watermarked PREVIEW) as a strip of thumbnails with the
 * sheet's problems as badges, a large page (PgUp / PgDn), the generate card ("N problems" with a link to them, the
 * cost on the button and the Lux available; problems never block generating), then the plan-set history. Back from
 * Buy Lux (checkout returns here), a prompt offers to generate with the Lux just bought. With gaps of a final set
 * (blocking checks) the button asks "Generate with open items?" first (OpenItemsConfirm): the PDF then leaves them as
 * fill-in blanks, never stamped, and the history offers their Open items checklist.
 */
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, ClipboardList, FileCheck2, Loader2, X, XCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { errorMessage } from "../api/client";
import { LuxGlyph } from "../components/LuxIcon";
import { Alert, Button, Card, Spinner, cx } from "../components/ui";
import { problemsText } from "../lib/permits";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import { luxText, generateState, sheetOfCheck } from "../lib/permitWorkspace";
import { usePreview } from "./data";
import { History } from "./History";
import { SolarAppPanel } from "./SolarAppPanel";
import { OpenItemsConfirm } from "./OpenItemsConfirm";
import { gapsOf } from "../lib/permitEquipment";
import { StepHead, useStartGenerate, type Ws } from "./PermitWorkspace";

export function ReviewStep({ ws }: { ws: Ws }) {
  const t = useT();
  const pages = usePreview(ws.designId, ws.version, ws.body, 1000, ws.preview.data?.rev ?? 0);
  const thumbs = pages.data?.thumbnails ?? [];
  const sheets = pages.data?.sheets ?? ws.preview.data?.sheets ?? [];
  const [page, setPage] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const { start, generate } = useStartGenerate(ws);
  const total = ws.statuses.review;
  const items = ws.planSets.data?.items ?? [];
  const cost = ws.preview.data?.cost_lux ?? ws.planSets.data?.cost_lux ?? 0;
  const enabled = ws.preview.data?.enabled ?? ws.planSets.data?.enabled ?? true;
  const gaps = gapsOf(ws.checks);
  const [confirming, setConfirming] = useState(false);
  const g = generateState(t, { enabled, canEdit: ws.canEdit, cost, hasHistory: items.length > 0, ready: !!ws.preview.data && !ws.preview.isFetching, gaps: gaps.length });
  const go = (anchor: Element | null) => (gaps.length ? setConfirming(true) : void start(anchor, cost));

  useEffect(() => {
    ws.sheetNav.current = (dir) => setPage((p) => Math.max(0, Math.min(thumbs.length - 1, p + dir)));
    ws.generateRef.current = () => {
      cardRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      window.setTimeout(() => buttonRef.current?.querySelector("button")?.focus(), 250);
    };
    return () => {
      ws.sheetNav.current = null;
      ws.generateRef.current = null;
    };
  });
  // problems by sheet, for the thumbnails' badges
  const bySheet = useMemo(() => {
    const out: Record<string, number> = {};
    for (const c of ws.checks) if (c.status === "problem") out[sheetOfCheck(c.id)] = (out[sheetOfCheck(c.id)] ?? 0) + 1;
    return out;
  }, [ws.checks]);
  const current = thumbs[Math.min(page, Math.max(0, thumbs.length - 1))];
  const sheetTitle = (sheet: string) => sheets.find((s) => s.sheet === sheet)?.title ?? (sheet === "A-1" ? t("permit.review.sitePhotos") : sheet);
  const firstProblem = ws.checks.find((c) => c.status === "problem" && !c.blocking);

  return (
    <div className="pw-step-body">
      <StepHead
        title={t("permit.step.review")}
        description={
          sheets.length
            ? `${t("permit.review.description")} ${thumbs.length ? t("permit.review.pagesAndSheets", { pages: thumbs.length, sheets: sheets.length }) : t("permit.review.pagesPendingSheets", { sheets: sheets.length })}`
            : t("permit.review.description")
        }
        status={ws.statuses.review}
      />
      {ws.purchase && ws.canEdit && (
        <div className={cx("pw-purchase", ws.purchase.waiting && "waiting")} role="status" aria-live="polite">
          <span className="pw-purchase-mark">{ws.purchase.waiting ? <Loader2 size={20} className="pw-spin" /> : <LuxGlyph size={22} />}</span>
          <div className="pw-purchase-text">
            <b>{ws.purchase.title}</b>
            <span>{ws.purchase.detail}</span>
            <span className="small muted">{t("permit.review.purchaseSaved")}</span>
          </div>
          <div className="pw-purchase-actions">
            {!ws.purchase.waiting && ws.purchase.canGenerate && (
              <Button variant="primary" icon={<FileCheck2 size={16} />} loading={generate.isPending} disabled={g.disabled} onClick={(e) => go(e.currentTarget)}>
                {g.label}
              </Button>
            )}
            <Button variant="ghost" onClick={ws.dismissPurchase}>{t("permit.review.notNow")}</Button>
          </div>
          <button className="icon-btn pw-purchase-close" onClick={ws.dismissPurchase} aria-label={t("permit.review.dismiss")}>
            <X size={16} />
          </button>
        </div>
      )}
      <div className="pw-review">
        <div className="pw-review-pages">
          <Card pad={false} className="pw-pages-card">
            <div className="pw-pages-head">
              <b>{current ? `${current.sheet} · ${sheetTitle(current.sheet)}` : t("permit.review.pages")}</b>
              <span className="muted small">{thumbs.length ? t("permit.review.pageOf", { page: page + 1, total: thumbs.length }) : ""}</span>
              <span className="row" style={{ gap: 4, marginInlineStart: "auto" }}>
                <button className="icon-btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page <= 0} aria-label={t("permit.review.previousPage")} title={t("permit.review.previousPageHint")}>
                  <DirIcon><ChevronLeft size={18} /></DirIcon>
                </button>
                <button className="icon-btn" onClick={() => setPage((p) => Math.min(thumbs.length - 1, p + 1))} disabled={page >= thumbs.length - 1} aria-label={t("permit.review.nextPage")} title={t("permit.review.nextPageHint")}>
                  <DirIcon><ChevronRight size={18} /></DirIcon>
                </button>
              </span>
            </div>
            <div className={cx("pw-page-large", pages.isFetching && "pw-stale")}>
              {current ? <img src={current.png} alt={t("permit.review.pageAlt", { sheet: current.sheet, title: sheetTitle(current.sheet) })} /> : pages.error ? <Alert tone="danger" title={t("permit.review.renderFailed")}>{errorMessage(pages.error)} <button className="link-btn" onClick={() => pages.refetch()}>{t("permit.review.retry")}</button></Alert> : <Spinner label={t("permit.review.rendering")} />}
            </div>
            <div className="pw-thumb-strip" role="listbox" aria-label={t("permit.review.pages")}>
              {thumbs.map((th, i) => {
                const b = bySheet[th.sheet];
                const firstOfSheet = thumbs.findIndex((x) => x.sheet === th.sheet) === i;
                return (
                  <button key={th.page} role="option" aria-selected={i === page} className={cx("pw-page-thumb", i === page && "on")} onClick={() => setPage(i)} title={`${th.sheet} ${sheetTitle(th.sheet)}`}>
                    <img src={th.png} alt="" loading="lazy" />
                    <span className="pw-page-label">{th.sheet}</span>
                    {b && firstOfSheet ? <span className="pw-page-badge fail" title={problemsText(t, b)}>{b} ✕</span> : null}
                  </button>
                );
              })}
              {!thumbs.length && [0, 1, 2, 3, 4, 5].map((i) => <span key={i} className="pw-page-thumb skeleton" />)}
            </div>
          </Card>
        </div>

        <div className="pw-generate" ref={cardRef}>
          <Card title={t("permit.review.readyTitle")}>
            {gaps.length > 0 && (
              <div className="pw-ready-line fail pw-gaps-line">
                <AlertTriangle size={16} className="pw-ic fail" />
                <span>
                  <FormattedMessage
                    id="permit.review.gapsLine"
                    values={{
                      count: gaps.length,
                      b: (c) => <b>{c}</b>,
                      link: (c) => (
                        <button className="link-btn" onClick={() => ws.locate(gaps[0]!.id)}>
                          {c}
                        </button>
                      ),
                    }}
                  />
                  <div className="small muted">{t("permit.review.gapsHint")}</div>
                </span>
              </div>
            )}
            <div className={cx("pw-ready-line", total.problems - gaps.length ? "fail" : "ok")}>
              {total.problems - gaps.length ? <XCircle size={16} className="pw-ic fail" /> : <CheckCircle2 size={16} className="pw-ic ok" />}
              {total.problems - gaps.length ? (
                <span>
                  <FormattedMessage
                    id="permit.review.problemsLine"
                    values={{
                      count: total.problems - gaps.length,
                      b: (c) => <b>{c}</b>,
                      link: (c) => (
                        <button className="link-btn" onClick={() => firstProblem && ws.locate(firstProblem.id)}>
                          {c}
                        </button>
                      ),
                    }}
                  />
                </span>
              ) : (
                <span>
                  <FormattedMessage id="permit.review.noProblems" values={{ count: total.ok, b: (c) => <b>{c}</b> }} />
                </span>
              )}
            </div>
            <div className="pw-cost">
              {cost > 0 ? (
                <>
                  <b>{t("permit.review.cost", { lux: cost / 100 })}</b>
                  {ws.credits != null && <span className="muted"> · {t("permit.review.available", { lux: Math.max(ws.credits, 0) / 100 })}</span>}
                  <div className="small muted">{t("permit.review.chargedOnce")}</div>
                </>
              ) : (
                <>
                  <b>{t("permit.review.free")}</b>
                  <div className="small muted">{t("permit.review.paidFree")}</div>
                </>
              )}
            </div>
            <div ref={buttonRef}>
            <Button
              variant="primary"
              block
              icon={gaps.length ? <ClipboardList size={16} /> : <FileCheck2 size={16} />}
              loading={generate.isPending}
              disabled={g.disabled}
              onClick={(e) => go(e.currentTarget)}
            >
              {g.label}
            </Button>
            </div>
            {g.reason && ws.canEdit && <p className="small muted pw-reason">{g.reason}</p>}
          </Card>
          {confirming && (
            <OpenItemsConfirm
              gaps={gaps}
              costLabel={cost > 0 ? luxText(t, cost) : null}
              busy={generate.isPending}
              onClose={() => setConfirming(false)}
              onLocate={(id) => ws.locate(id)}
              onConfirm={(anchor) => {
                setConfirming(false);
                void start(anchor, cost, true);
              }}
            />
          )}
        </div>
      </div>
      <SolarAppPanel ws={ws} />
      <History ws={ws} />
    </div>
  );
}
